import React from "react";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Action from "../../components/Action";
import Card from "../../components/Card";
import QuoteGuarantees from "../QuoteCommon/QuoteGuarantees";
import List, { ListItem } from "../../components/List";
import { formatMoneyWithCurrency, moneyStrToNum } from "../../shared/utils/moneyUtils";
import Image from "../../components/Image";
import imageRelaxingOnSofa from "../../images/relaxing-on-sofa.png";
import ReferralCodeBanner from "../../components/ReferralCodeBanner";
import { ListItemSuffix, QuoteBreakdown, QuotedPrice, QuotedPriceWrapper } from "../QuoteCommon/QuoteStyles";

import { isConnellsReferral } from "../ReferrerQuote/utils/ReferrerQuoteUtils";

function ReferrerQuoteSentPage(props) {
  const {
    buyingDetails,
    defaultChannel,
    inBranchReferral,
    recipient,
    referral,
    quoteDetails,
    transactionType,
    sellingDetails,
  } = props;

  const showReferralCodeBanner = !inBranchReferral;

  let channel = defaultChannel;
  if (referral?.referralSource) {
    channel = referral.referralSource;
  }
  // Channel here is the lead source, e.g. home_conveyancing_seller
  const connellsCase = isConnellsReferral(channel);

  let saleLegalFee = "";
  let salePropertyPrice = "";
  let purchaseLegalFee = "";
  let purchasePropertyPrice = "";
  let quotePrice = "";
  let quotePriceText = "";
  let stampDuty = "";
  let stampDutyDescription = "Stamp Duty (paid to the government)";
  let landRegistryFee = "";
  let totalFeesAndTaxes = "";

  let includeDecimal = landRegistryFee.includes(".") || stampDuty.includes(".");

  if (quoteDetails) {
    quotePrice = formatMoneyWithCurrency(moneyStrToNum(quoteDetails.totalPrice).toString(), includeDecimal);

    saleLegalFee = formatMoneyWithCurrency(moneyStrToNum(quoteDetails.sellingQuotePrice).toString(), includeDecimal);

    purchaseLegalFee = formatMoneyWithCurrency(moneyStrToNum(quoteDetails.buyingQuotePrice).toString(), includeDecimal);

    stampDuty = formatMoneyWithCurrency(moneyStrToNum(quoteDetails.stampDutyFee).toString(), includeDecimal);

    landRegistryFee = formatMoneyWithCurrency(moneyStrToNum(quoteDetails.landRegistryFee).toString(), includeDecimal);

    totalFeesAndTaxes = formatMoneyWithCurrency(
      moneyStrToNum(quoteDetails.totalFeesAndTaxes).toString(),
      includeDecimal,
    );

    purchasePropertyPrice =
      transactionType === "Buying" || transactionType === "BuyingAndSelling"
        ? formatMoneyWithCurrency(moneyStrToNum(buyingDetails.price).toString(), includeDecimal)
        : "";

    salePropertyPrice =
      transactionType === "Selling" || transactionType === "BuyingAndSelling"
        ? formatMoneyWithCurrency(moneyStrToNum(sellingDetails.price).toString(), includeDecimal)
        : "";
  }

  function getTenure(details) {
    if (details?.tenure === "leasehold with share of freehold") {
      return "share of freehold";
    }
    return details.tenure;
  }

  let sellingTenure = "";
  let buyingTenure = "";

  if (transactionType === "Selling" || transactionType === "BuyingAndSelling") {
    sellingTenure = getTenure(sellingDetails);
  }
  if (transactionType === "Buying" || transactionType === "BuyingAndSelling") {
    buyingTenure = getTenure(buyingDetails);
  }

  switch (transactionType) {
    case "Buying":
      quotePriceText = `That covers all our legal work on their ${buyingTenure} purchase at ${purchasePropertyPrice}, including VAT.`;
      break;
    case "Selling":
      quotePriceText = `That covers all our legal work on their ${sellingTenure} sale at ${salePropertyPrice}, including VAT.`;
      break;
    case "BuyingAndSelling":
      quotePriceText = `That covers all our legal work on their ${buyingTenure} purchase at ${purchasePropertyPrice} and ${sellingTenure} sale at ${salePropertyPrice}, including VAT.`;
      break;
    default:
      console.warn(`ReferrerQuoteSentPage: Unhandled transaction type "${transactionType}"`);
      break;
  }

  let cardTitle, introText;
  let showQuote = true;
  const recipientData = {
    agent: {
      cardTitle: "Thanks, now check your email",
      introText:
        "In a couple of minutes you’ll get an email from Juno with a link to enter information about your client.",
      showQuote: false,
    },
    default: {
      cardTitle: "Done. We’ve emailed the quote to your client",
      introText:
        "We’ve emailed the quote and will call your client to talk it through. You’ll get an email when they sign up.",
      showQuote: true,
    },
  };

  if (inBranchReferral) {
    cardTitle = "Thanks, we’ve prepared a quote for your client";
    introText = "One of our sales team will get in touch with your client to confirm the details and send the quote";
  } else {
    const data = recipientData[recipient] || recipientData.default;
    cardTitle = data.cardTitle;
    introText = data.introText;
    showQuote = data.showQuote;
  }

  return (
    <React.Fragment>
      <ScrollToTop />
      <Card title={cardTitle} removeTopPadding>
        <p>{introText}</p>

        {showQuote && (
          <React.Fragment>
            <QuotedPriceWrapper>
              <QuotedPrice>{quotePrice}</QuotedPrice>
              <p>{quotePriceText}</p>
              {!connellsCase && <QuoteGuarantees transactionType={transactionType} />}
            </QuotedPriceWrapper>

            {!!referral && showReferralCodeBanner && (
              <ReferralCodeBanner text={referral.quoteMessage} withinQuote={true} />
            )}

            {transactionType === "Buying" || transactionType === "BuyingAndSelling" ? (
              <QuoteBreakdown>
                <List>
                  {transactionType === "BuyingAndSelling" ? (
                    <>
                      <ListItem divider>
                        Juno’s fee for the {sellingTenure} sale
                        <ListItemSuffix>{saleLegalFee}</ListItemSuffix>
                      </ListItem>
                    </>
                  ) : (
                    <></>
                  )}

                  <ListItem divider>
                    Juno’s fee for the {buyingTenure} purchase
                    <ListItemSuffix>{purchaseLegalFee}</ListItemSuffix>
                  </ListItem>
                  <ListItem divider>
                    Land Registry fee (paid to the government)
                    <ListItemSuffix data-testid="land-registry-fee">{landRegistryFee}</ListItemSuffix>
                  </ListItem>
                  <ListItem divider>
                    {stampDutyDescription}
                    <ListItemSuffix data-testid="stamp-duty-fee">{stampDuty}</ListItemSuffix>
                  </ListItem>
                  <ListItem divider>
                    Total, including government fees and taxes
                    <ListItemSuffix data-testid="total-fees-and-taxes">{totalFeesAndTaxes}</ListItemSuffix>
                  </ListItem>
                </List>
              </QuoteBreakdown>
            ) : null}
          </React.Fragment>
        )}

        <Action onClick={() => props.restartFlow()}>Refer another client</Action>
      </Card>

      <Image src={imageRelaxingOnSofa} fullWidth />
    </React.Fragment>
  );
}

export default ReferrerQuoteSentPage;
